import React from 'react';
import classNames from 'classnames/bind';
import { useToggle } from '@mantine/hooks';

import Pipeline from 'types/pipeline';

import { useDeploymentForm } from 'deployments/components/Form';
import { useHasAccess } from 'hooks/useHasAccess';
import { useMarketplaceModels, useModels } from 'hooks/api/useModels';
import { usePipelineComponents } from 'pipelines/hooks/usePipelineComponents';
import { usePipelines } from 'pipelines/hooks/usePipelines';
import { useUsageLimits } from 'organizations/hooks/useUsageLimits';
import { type VideoSource } from 'hooks/api/useVideoSources';

import { Accordion } from 'components/Accordion/Accordion';
import { DeploymentFormContentRef } from 'deployments/components/Form/Content';
import { DeploymentFormSkeleton } from 'deployments/components/Form/Skeleton';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { PipelinePicker } from 'pipelines/components/PipelinePicker/PipelinePicker';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';

import { useRapidDeployState } from '../hooks/useRapidDeployState';
import { RapidDeployConfigurationFormContent } from './ConfigurationFormContent';
import styles from '../RapidDeploy.module.scss';

const c = classNames.bind(styles);

export type RapidDeployConfigurationFormProps = {
  videoSource: VideoSource;
  defaultOpen?: boolean;
  onPipelineSelect: () => void;
  isVirtualCameraSource?: boolean;
  effectiveSourceName: string | undefined;
};

function RapidDeployConfigurationForm(
  {
    videoSource,
    defaultOpen,
    onPipelineSelect,
    isVirtualCameraSource,
    effectiveSourceName,
  }: RapidDeployConfigurationFormProps,
  ref: React.ForwardedRef<DeploymentFormContentRef>
) {
  const { gateway } = useRapidDeployState();
  const { pipeline, setPipeline } = useDeploymentForm();
  const [hasAccess] = useHasAccess();
  const isOpenDefault = defaultOpen ?? false;
  const [isOpen, toggleIsOpen] = useToggle([isOpenDefault, !isOpenDefault]);

  const { data: pipelines } = usePipelines();
  const hasPipelines = pipelines ? pipelines.total_elements > 0 : false;

  const isVirtualCameraWithoutUploads =
    !('stream_type' in videoSource) && videoSource.conn_type === 'virtual';

  // Preload
  const { isLoading: isLoadingUsageLimits, error: usageLimitsError } =
    useUsageLimits({
      enabled: hasAccess('deploy_edit'),
    });
  const [, { isLoading: isLoadingPipelineComponents }] =
    usePipelineComponents();
  const { isLoading: isLoadingModels } = useModels();
  const { isLoading: isLoadingMarketplaceModels } = useMarketplaceModels();

  const [isValid, setIsValid] = React.useState(false);

  const isLoading =
    isLoadingPipelineComponents ||
    isLoadingModels ||
    isLoadingMarketplaceModels ||
    isLoadingUsageLimits;

  if (!gateway) {
    return null;
  }

  if (usageLimitsError) {
    return (
      <FormMessage intent="danger" icon="warning">
        <strong>
          {usageLimitsError.message || 'An unknown error occurred.'}
        </strong>
        <br />
        Please try again later.
      </FormMessage>
    );
  }

  function handlePipelineSelect(pipeline: Pipeline) {
    setPipeline(pipeline);
    onPipelineSelect();
  }

  return (
    <Accordion
      className={c('deployments-form', { error: !isValid, isOpen })}
      label={
        <>
          <span>{effectiveSourceName ?? videoSource.name}</span>
          {!isOpen && (
            <span
              className={c('deployments-header-pipeline')}
              aria-label="Workflow"
            >
              {pipeline ? pipeline.name : 'No workflow selected'}
            </span>
          )}
          <StatusIndicator
            className={c('deployments-form-state')}
            status={pipeline && isValid ? 'valid' : 'invalid'}
            size="small"
          />
        </>
      }
      icon={'stream_type' in videoSource ? 'stream' : 'camera'}
      defaultOpen={defaultOpen}
      onOpenChange={toggleIsOpen}
    >
      <PipelinePicker
        pipeline={pipeline}
        hasPipelines={hasPipelines}
        onPipelineSelect={handlePipelineSelect}
      />
      {(isVirtualCameraSource || isVirtualCameraWithoutUploads) && (
        <FormMessage intent="info" icon="info">
          This workflow will be applied to every new clip that is uploaded from
          this Camera. You can subsequently update workflow configuration from
          the Camera settings view.
        </FormMessage>
      )}

      {isLoading && pipeline && (
        <SkeletonList component={<DeploymentFormSkeleton />} />
      )}

      {!isLoading && pipeline && (
        <RapidDeployConfigurationFormContent
          videoSource={videoSource}
          pipeline={pipeline}
          onIsValidChange={setIsValid}
          ref={ref}
        />
      )}
    </Accordion>
  );
}

const ForwardInnerRef = React.forwardRef<
  DeploymentFormContentRef,
  RapidDeployConfigurationFormProps
>(RapidDeployConfigurationForm);

export { ForwardInnerRef as RapidDeployConfigurationForm };
